import { FindingCard } from "./FindingCard";
import { useRunFindings } from "../../hooks/useRunFindings";

interface FindingsListProps {
  runId: string;
  expectedCount?: number;
  brandId?: string;
}

export function FindingsList({ runId, expectedCount, brandId }: FindingsListProps) {
  const { data, isLoading, isError, error } = useRunFindings(runId);
  const findings = data?.data ?? [];

  const skeletonCount = Math.max(1, Math.min(expectedCount ?? 3, 6));

  return (
    <section className="space-y-4" aria-labelledby="findings-heading">
      <div className="flex items-center justify-between">
        <h2 id="findings-heading" className="text-lg font-semibold text-slate-900">
          Findings
        </h2>
        {isLoading ? (
          <span className="text-sm text-slate-500" aria-live="polite" data-testid="findings-count-loading">
            <span aria-hidden="true">...</span>
            <span className="sr-only">Loading findings</span>
          </span>
        ) : isError ? (
          <span
            className="text-sm text-slate-500"
            aria-live="polite"
            data-testid="findings-count-error"
          >
            <span aria-hidden="true">—</span>
            <span className="sr-only">Findings count unavailable</span>
          </span>
        ) : (
          <span className="text-sm text-slate-500" data-testid="findings-count">{findings.length}</span>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3" aria-label="findings-loading">
          {Array.from({ length: skeletonCount }, (_, index) => (
            <div
              key={index}
              className="animate-pulse rounded-xl border border-slate-200 bg-white p-4"
            >
              <div className="mb-3 h-4 w-40 rounded bg-slate-200" />
              <div className="mb-2 h-3 w-full rounded bg-slate-100" />
              <div className="h-3 w-3/4 rounded bg-slate-100" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && !isError && findings.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-600">
          This run produced no findings.
        </div>
      )}

      {!isLoading && isError && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Failed to load findings.</p>
          <p className="mt-1 text-amber-800">
            {error instanceof Error ? error.message : "The findings request did not succeed."}
          </p>
          {expectedCount && expectedCount > 0 && (
            <p className="mt-2 text-amber-800">
              This run reports {expectedCount} finding{expectedCount === 1 ? "" : "s"}, but the details request failed.
            </p>
          )}
        </div>
      )}

      {!isLoading && !isError && findings.length > 0 && (
        <div className="space-y-3">
          {findings.map((finding) => (
            <FindingCard key={finding.id} finding={finding} brandId={brandId} />
          ))}
        </div>
      )}
    </section>
  );
}
